export const WELCOME_PATH = '/welcome';
export const WELCOME_EMIRATES_ID_LOGIN_METHOD = 'emirates-id';

const WELCOME_LOGIN_QUERY_KEYS = ['login', 'openLogin', 'method'] as const;

type QueryParamValue = string | string[] | undefined;

const EMIRATES_ID_METHODS = new Set([
  WELCOME_EMIRATES_ID_LOGIN_METHOD,
  'emirates_id',
]);

export function isTruthyQueryParam(value: QueryParamValue): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === '1' || raw === 'true';
}

export function isEmiratesIdLoginMethod(value: QueryParamValue): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) {
    return false;
  }
  return EMIRATES_ID_METHODS.has(raw.toLowerCase().trim());
}

function readLoginFromWindow(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      isTruthyQueryParam(params.get('login') ?? undefined) ||
      isTruthyQueryParam(params.get('openLogin') ?? undefined)
    );
  } catch {
    return false;
  }
}

function readEmiratesIdMethodFromWindow(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return isEmiratesIdLoginMethod(params.get('method') ?? undefined);
  } catch {
    return false;
  }
}

export function resolveWelcomeLoginIntent(input: {
  login?: QueryParamValue;
  openLogin?: QueryParamValue;
  method?: QueryParamValue;
}): { fromQuery: boolean; fromEmiratesId: boolean } {
  const fromEmiratesId =
    isEmiratesIdLoginMethod(input.method) || readEmiratesIdMethodFromWindow();
  const fromQuery =
    isTruthyQueryParam(input.login) ||
    isTruthyQueryParam(input.openLogin) ||
    fromEmiratesId ||
    readLoginFromWindow();
  return { fromQuery, fromEmiratesId };
}

/**
 * Update welcome URL query via history.replaceState (not router.replace)
 * so navigation listeners do not re-fire.
 */
function replaceWelcomeQuery(mutator: (params: URLSearchParams) => void): void {
  if (typeof window === 'undefined') {
    return;
  }
  const pathname = window.location.pathname.replace(/\/$/, '') || WELCOME_PATH;
  if (pathname !== WELCOME_PATH) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  mutator(params);
  const nextSearch = params.toString();
  const current = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search;
  if (nextSearch === current) {
    return;
  }

  const nextUrl = nextSearch
    ? `${window.location.pathname}?${nextSearch}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

/** Open login dialog → `?login=1` (keeps other params; drops emirates method). */
export function setWelcomeLoginQuery(): void {
  replaceWelcomeQuery((params) => {
    params.delete('openLogin');
    params.delete('method');
    params.set('login', '1');
  });
}

/** Open Emirates ID step → `?login=1&method=emirates-id`. */
export function setWelcomeEmiratesIdQuery(): void {
  replaceWelcomeQuery((params) => {
    params.delete('openLogin');
    params.set('login', '1');
    params.set('method', WELCOME_EMIRATES_ID_LOGIN_METHOD);
  });
}

/** Close dialog → remove login / openLogin / method. */
export function clearWelcomeLoginQueryFromUrl(): void {
  replaceWelcomeQuery((params) => {
    for (const key of WELCOME_LOGIN_QUERY_KEYS) {
      params.delete(key);
    }
  });
}
